import { useEffect, useState } from 'react';
import { collection, onSnapshot } from 'firebase/firestore';
import { db } from '../firebase';
import dexie from '../offlineDB';
import { normalizeType } from '../utils/normalize';

const isNotesModeItem = (item) => ((item?.mode || 'tunes') === 'notes') || normalizeType(item?.type) === 'note';
const isDeletedItem = (item) => Boolean(
  item?.deletedAt
  || item?.isDeleted
  || item?.deleted
  || item?.removedAt
  || item?.trashedAt
);

const normalizeNotes = (items = []) =>
  items
    .filter((item) => isNotesModeItem(item) && !isDeletedItem(item))
    .map((item) => ({
      id: item.id,
      title: item.title || '',
      notes: item.notes || '',
      genre: item.genre || '',
      type: item.type || '',
      status: item.status || '',
      rating: item.rating || '',
      ratingLabel: item.ratingLabel || '',
      eventDate: item.eventDate || '',
      quickNote: item.quickNote || '',
      customTags: Array.isArray(item.customTags) ? item.customTags : [],
      link1: item.link1 || '',
      link2: item.link2 || '',
      link3: item.link3 || '',
      links: Array.isArray(item.links) ? item.links : [],
      learnedFrom: item.learnedFrom || '',
      source: item.source || '',
      updatedAt: item.updatedAt || '',
      createdAt: item.createdAt || '',
    }))
    .filter((item) => Boolean(item.id));

const normalizeFolders = (items = []) =>
  items
    .filter((item) => (item?.mode || 'tunes') === 'notes' && !isDeletedItem(item))
    .map((item) => ({
      id: item.id,
      title: item.title || '',
      notes: item.notes || '',
      genre: item.genre || '',
      type: item.type || '',
      status: item.status || '',
      rating: item.rating || '',
      ratingLabel: item.ratingLabel || '',
      eventDate: item.eventDate || '',
      customTags: Array.isArray(item.customTags) ? item.customTags : [],
      learnedFrom: item.learnedFrom || '',
      source: item.source || '',
      updatedAt: item.updatedAt || '',
      createdAt: item.createdAt || '',
    }))
    .filter((item) => Boolean(item.id));

export default function useInternalLinkTargets(user, enabled = true) {
  const [notes, setNotes] = useState([]);
  const [folders, setFolders] = useState([]);

  useEffect(() => {
    if (!enabled || !user?.uid) {
      setNotes([]);
      setFolders([]);
      return undefined;
    }

    let disposed = false;

    const preloadOffline = async () => {
      try {
        const [offlineTunes, offlineSets] = await Promise.all([
          dexie.tunes.toArray(),
          dexie.sets.toArray(),
        ]);
        if (disposed) return;
        setNotes(normalizeNotes(offlineTunes));
        setFolders(normalizeFolders(offlineSets));
      } catch (err) {
        console.warn('Failed to load offline link targets:', err);
      }
    };

    if (!navigator.onLine) {
      preloadOffline();
    }

    const unsubTunes = onSnapshot(
      collection(db, 'users', user.uid, 'tunes'),
      (snapshot) => {
        if (disposed) return;
        const items = snapshot.docs.map((doc) => ({ id: doc.id, ...doc.data() }));
        setNotes(normalizeNotes(items));
      },
      (err) => {
        console.warn('Failed to subscribe to note link targets:', err);
      }
    );

    const unsubSets = onSnapshot(
      collection(db, 'users', user.uid, 'sets'),
      (snapshot) => {
        if (disposed) return;
        const items = snapshot.docs.map((doc) => ({ id: doc.id, ...doc.data() }));
        setFolders(normalizeFolders(items));
      },
      (err) => {
        console.warn('Failed to subscribe to folder link targets:', err);
      }
    );

    return () => {
      disposed = true;
      unsubTunes();
      unsubSets();
    };
  }, [enabled, user?.uid]);

  return { notes, folders };
}
